import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Typography,
  useTheme,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { MessageMenuItem } from "../../../types";
import { menuItemStyles } from "./styles";

function usePrefersHoverSubmenu(): boolean {
  const [prefers, setPrefers] = useState(true);
  useEffect(() => {
    const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
    const apply = () => setPrefers(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);
  return prefers;
}

function menuDescription(item: MessageMenuItem): string | undefined {
  const d = item.meta?.description;
  return typeof d === "string" ? d : undefined;
}

export function fireMessageMenuAction(
  item: MessageMenuItem,
  textToSend: string,
  handleSendMenuItem: (
    text: string,
    description: string,
    chosen_text?: string,
  ) => void,
): void {
  if (item.disabled || item.kind === "submenu") return;
  const description = menuDescription(item) || textToSend || item.label;
  handleSendMenuItem(item.label, description, textToSend);
}

type MessageMenuVisualisationProps = {
  items: MessageMenuItem[];
  selectedText?: string;
  text?: string;
  handleSendMenuItem: (
    text: string,
    description: string,
    chosen_text?: string,
  ) => void;
};

function ActionMenuRow({
  item,
  onActivate,
}: {
  item: MessageMenuItem;
  onActivate: (item: MessageMenuItem) => void;
}) {
  const desc = menuDescription(item);

  return (
    <MenuItem
      disabled={item.disabled}
      onClick={() => onActivate(item)}
      disableRipple
      sx={menuItemStyles}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Box>
        <Typography sx={{ fontSize: { xs: "12px", md: "14px" } }}>
          {item.label}
        </Typography>
        {desc ? (
          <Typography
            variant="body2"
            color="grey.600"
            sx={{
              fontSize: { xs: "0.7rem", md: "0.875rem" },
              mt: { xs: 0.125, md: 0.25 },
            }}
          >
            {desc}
          </Typography>
        ) : null}
      </Box>
    </MenuItem>
  );
}

const SUBMENU_CLOSE_MS = 280;

function relatedNode(
  target: EventTarget | null | undefined,
): Node | null {
  return target instanceof Node ? target : null;
}

function isInside(el: Node | null | undefined, target: Node | null): boolean {
  if (!el || !target) return false;
  return el === target || el.contains(target);
}

type OpenSubmenuState = { id: string; anchor: HTMLElement };

function SubmenuRow({
  item,
  renderChildren,
  openSubmenu,
  setOpenSubmenu,
}: {
  item: MessageMenuItem;
  renderChildren: (children: MessageMenuItem[]) => React.ReactNode;
  openSubmenu: OpenSubmenuState | null;
  setOpenSubmenu: React.Dispatch<React.SetStateAction<OpenSubmenuState | null>>;
}) {
  const theme = useTheme();
  const prefersHover = usePrefersHoverSubmenu();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const paperRef = useRef<HTMLDivElement | null>(null);
  const children = item.children ?? [];

  const isThisOpen = openSubmenu?.id === item.id;
  const anchor = isThisOpen ? openSubmenu.anchor : null;

  const clearTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openAt = (el: HTMLElement | null) => {
    clearTimer();
    if (el === null) {
      setOpenSubmenu((prev) => (prev?.id === item.id ? null : prev));
    } else {
      setOpenSubmenu({ id: item.id, anchor: el });
    }
  };

  const scheduleClose = () => {
    clearTimer();
    const closingId = item.id;
    closeTimer.current = setTimeout(() => {
      setOpenSubmenu((prev) => (prev?.id === closingId ? null : prev));
      closeTimer.current = null;
    }, SUBMENU_CLOSE_MS);
  };

  useEffect(() => () => clearTimer(), []);

  const submenuOpen = Boolean(anchor) && children.length > 0;
  const isDark = theme.palette.mode === "dark";

  const nestedMenuListSx = {
    padding: 0,
    outline: 0,
    "& .MuiMenuItem-root": {
      backgroundColor: theme.palette.messageMenuDropdown.bg,
      borderTop: `1px solid ${theme.palette.messageMenuDropdown.border}`,
    },
    "& .MuiMenuItem-root:first-of-type": {
      borderTop: "none",
    },
  };

  return (
    <>
      <MenuItem
        disabled={item.disabled || children.length === 0}
        disableRipple
        sx={{
          ...menuItemStyles(theme),
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseEnter={(e) => {
          if (prefersHover) openAt(e.currentTarget);
        }}
        onMouseLeave={(e) => {
          if (!prefersHover) return;
          const next = relatedNode(e.relatedTarget);
          if (isInside(paperRef.current, next)) return;
          scheduleClose();
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          if (!prefersHover) {
            openAt(
              isThisOpen ? null : (e.currentTarget as HTMLElement),
            );
          }
        }}
      >
        <Typography
          sx={{ fontSize: { xs: "13px", sm: "14px" }, flex: 1 }}
        >
          {item.label}
        </Typography>
        <ChevronRightIcon
          sx={{ opacity: 0.7, fontSize: { xs: 18, sm: 20 } }}
        />
      </MenuItem>
      <Popper
        open={submenuOpen}
        anchorEl={anchor}
        placement="right-start"
        transition={false}
        disablePortal={false}
        modifiers={[
          { name: "offset", options: { offset: [-14, 0] } },
          {
            name: "preventOverflow",
            options: { altAxis: true, tetherOffset: 8 },
          },
        ]}
        sx={{
          zIndex: (t) => t.zIndex.tooltip + 2,
          pointerEvents: submenuOpen ? "auto" : "none",
        }}
      >
        <Paper
          ref={paperRef}
          className="message-menu-subpopover"
          elevation={isDark ? 0 : 8}
          onMouseDown={(e) => e.stopPropagation()}
          onMouseEnter={() => clearTimer()}
          onMouseLeave={(e: React.MouseEvent) => {
            if (!prefersHover) return;
            const next = relatedNode(e.relatedTarget);
            if (anchor && isInside(anchor, next)) return;
            scheduleClose();
          }}
          sx={{
            borderRadius: "18px",
            overflow: "hidden",
            backgroundColor: theme.palette.messageMenuDropdown.bg,
            ...(isDark
              ? {
                  border: "none",
                  boxShadow: "0 4px 18px rgba(0, 0, 0, 0.38)",
                }
              : {
                  border: `1px solid ${theme.palette.messageMenuDropdown.border}`,
                  boxShadow: theme.shadows[8],
                }),
          }}
        >
          <MenuList
            autoFocus={false}
            autoFocusItem={false}
            disableListWrap
            sx={nestedMenuListSx}
          >
            {renderChildren(children)}
          </MenuList>
        </Paper>
      </Popper>
    </>
  );
}

export function MessageMenuVisualisation({
  items,
  selectedText,
  text,
  handleSendMenuItem,
}: MessageMenuVisualisationProps) {
  const textToSend = selectedText || text || "";
  const [openSubmenu, setOpenSubmenu] = useState<OpenSubmenuState | null>(null);

  const onAction = (item: MessageMenuItem) => {
    fireMessageMenuAction(item, textToSend, handleSendMenuItem);
  };

  const renderNodes = (nodes: MessageMenuItem[]): React.ReactNode =>
    nodes.map((item) => {
      const branch =
        item.kind === "submenu" ||
        (item.children?.length ?? 0) > 0;

      if (branch) {
        return (
          <SubmenuRow
            key={item.id}
            item={
              item.kind === "submenu"
                ? item
                : { ...item, kind: "submenu", children: item.children }
            }
            renderChildren={renderNodes}
            openSubmenu={openSubmenu}
            setOpenSubmenu={setOpenSubmenu}
          />
        );
      }

      return (
        <ActionMenuRow key={item.id} item={item} onActivate={onAction} />
      );
    });

  return <>{renderNodes(items)}</>;
}
